<div class="flex max-w-7xl flex-col gap-6">
  <!-- Act4Travel Scanner -->
  <app-section
    [title]="'help.howItWorks.sections.act4travel.title' | transloco"
    lucidIconRef="leaf"
  >
    <div class="flex flex-col gap-4 pt-4">
      <p class="text-paragraph-reg text-greyscale-700">
        {{ "help.howItWorks.sections.act4travel.intro" | transloco }}
      </p>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        @for (step of act4travelSteps; track step.number) {
          <div class="rounded-lg border border-primary-200 bg-primary-50 p-4">
            <div class="mb-3 flex items-center gap-3">
              <div
                class="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-paragraph-s-reg font-bold text-white"
              >
                {{ step.number }}
              </div>
              <h3 class="text-heading-5 font-semibold text-greyscale-900">
                {{ step.titleKey | transloco }}
              </h3>
            </div>
            <p class="text-paragraph-s-reg text-greyscale-600">
              {{ step.descriptionKey | transloco }}
            </p>
          </div>
        }
      </div>
    </div>
  </app-section>

  <!-- 4 Steps -->

  <h3 class="text-heading-3 text-greyscale-900">
    {{ "help.howItWorks.howItWorks" | transloco }}
  </h3>
  <!-- Data Input Methods -->
  <app-section
    [title]="'help.howItWorks.sections.dataInput.title' | transloco"
    lucidIconRef="upload"
  >
    <div class="flex flex-col gap-4 pt-4">
      <p class="text-paragraph-reg text-greyscale-700">
        {{ "help.howItWorks.sections.dataInput.intro" | transloco }}
      </p>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        @for (method of dataInputMethods; track method.titleKey) {
          <div
            class="rounded-lg border border-greyscale-200 bg-white p-5 transition-colors hover:border-primary-300"
          >
            <div
              class="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-primary-200"
            >
              <lucide-icon
                [name]="method.icon"
                class="text-primary-500"
                [size]="20"
              />
            </div>
            <h4 class="text-heading-5 mb-2 font-semibold text-greyscale-900">
              {{ method.titleKey | transloco }}
            </h4>
            <p class="text-paragraph-s-reg text-greyscale-600">
              {{ method.descriptionKey | transloco }}
            </p>
          </div>
        }
      </div>
    </div>
  </app-section>

  <!-- Agrégation des résultats -->
  <app-section
    [title]="'help.howItWorks.sections.aggregation.title' | transloco"
    lucidIconRef="bar-chart-2"
  >
    <div class="flex flex-col gap-4 pt-4">
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <!-- XLS Report -->
        <div class="rounded-lg border border-greyscale-200 p-4">
          <div class="mb-3 flex items-center gap-2">
            <lucide-icon name="list" class="text-primary-500" [size]="20" />
            <h3 class="text-heading-5 font-semibold text-greyscale-900">
              {{ "help.howItWorks.sections.aggregation.xls.title" | transloco }}
            </h3>
          </div>
          <p class="text-paragraph-s-reg text-greyscale-700">
            {{
              "help.howItWorks.sections.aggregation.xls.description" | transloco
            }}
          </p>
        </div>

        <!-- PDF Report -->
        <div class="rounded-lg border border-greyscale-200 p-4">
          <div class="mb-3 flex items-center gap-2">
            <lucide-icon name="download" class="text-primary-500" [size]="20" />
            <h3 class="text-heading-5 font-semibold text-greyscale-900">
              {{ "help.howItWorks.sections.aggregation.pdf.title" | transloco }}
            </h3>
          </div>
          <p class="text-paragraph-s-reg text-greyscale-700">
            {{
              "help.howItWorks.sections.aggregation.pdf.description" | transloco
            }}
          </p>
        </div>
      </div>
    </div>
  </app-section>

  <!-- AI Module -->
  <app-section
    [title]="'help.howItWorks.sections.aiModule.title' | transloco"
    lucidIconRef="bot"
  >
    <div class="flex flex-col gap-4 pt-4">
      <!-- What model -->
      <h3 class="mb-3 text-heading-4 font-semibold text-greyscale-900">
        {{ "help.howItWorks.sections.aiModule.model.title" | transloco }}
      </h3>
      <p class="mb-6 text-paragraph-reg text-greyscale-700">
        {{ "help.howItWorks.sections.aiModule.model.description" | transloco }}
      </p>

      <!-- Purpose -->
      <h3 class="mb-3 text-heading-4 font-semibold text-greyscale-900">
        {{ "help.howItWorks.sections.aiModule.purpose.title" | transloco }}
      </h3>
      <p class="mb-3 text-paragraph-reg text-greyscale-700">
        {{ "help.howItWorks.sections.aiModule.purpose.intro" | transloco }}
      </p>
      <ul class="mb-3 space-y-2">
        <li
          class="flex items-start gap-2 text-paragraph-s-reg text-greyscale-700"
        >
          <lucide-icon
            name="check"
            class="mt-0.5 shrink-0 text-primary-500"
            [size]="16"
          />
          <span>{{
            "help.howItWorks.sections.aiModule.purpose.probabilistic"
              | transloco
          }}</span>
        </li>
        <li
          class="flex items-start gap-2 text-paragraph-s-reg text-greyscale-700"
        >
          <lucide-icon
            name="check"
            class="mt-0.5 shrink-0 text-primary-500"
            [size]="16"
          />
          <span>{{
            "help.howItWorks.sections.aiModule.purpose.engine" | transloco
          }}</span>
        </li>
        <li
          class="flex items-start gap-2 text-paragraph-s-reg text-greyscale-700"
        >
          <lucide-icon
            name="check"
            class="mt-0.5 shrink-0 text-primary-500"
            [size]="16"
          />
          <span>{{
            "help.howItWorks.sections.aiModule.purpose.parameters" | transloco
          }}</span>
        </li>
        <li
          class="flex items-start gap-2 text-paragraph-s-reg text-greyscale-700"
        >
          <lucide-icon
            name="check"
            class="mt-0.5 shrink-0 text-primary-500"
            [size]="16"
          />
          <span>{{
            "help.howItWorks.sections.aiModule.purpose.noStorage" | transloco
          }}</span>
        </li>
      </ul>
      <div class="mb-6 rounded-lg border border-warning-200 bg-warning-50 p-3">
        <div class="flex items-start gap-2">
          <lucide-icon
            name="triangle-alert"
            class="mt-0.5 shrink-0 text-warning-600"
            [size]="16"
          />
          <p class="text-paragraph-s-reg text-warning-800">
            {{
              "help.howItWorks.sections.aiModule.purpose.noAutonomy" | transloco
            }}
          </p>
        </div>
      </div>

      <!-- Results interpretation -->
      <h3 class="mb-3 text-heading-4 font-semibold text-greyscale-900">
        {{ "help.howItWorks.sections.aiModule.results.title" | transloco }}
      </h3>
      <p class="mb-3 text-paragraph-reg text-greyscale-700">
        {{ "help.howItWorks.sections.aiModule.results.intro" | transloco }}
      </p>
      <ul class="mb-3 space-y-2">
        <li
          class="flex items-start gap-2 text-paragraph-s-reg text-greyscale-700"
        >
          <lucide-icon
            name="check"
            class="mt-0.5 shrink-0 text-primary-500"
            [size]="16"
          />
          <span>{{
            "help.howItWorks.sections.aiModule.results.helper" | transloco
          }}</span>
        </li>
        <li
          class="flex items-start gap-2 text-paragraph-s-reg text-greyscale-700"
        >
          <lucide-icon
            name="check"
            class="mt-0.5 shrink-0 text-primary-500"
            [size]="16"
          />
          <span>{{
            "help.howItWorks.sections.aiModule.results.approximations"
              | transloco
          }}</span>
        </li>
        <li
          class="flex items-start gap-2 text-paragraph-s-reg text-greyscale-700"
        >
          <lucide-icon
            name="check"
            class="mt-0.5 shrink-0 text-primary-500"
            [size]="16"
          />
          <span>{{
            "help.howItWorks.sections.aiModule.results.noGuarantee" | transloco
          }}</span>
        </li>
      </ul>
      <p class="text-paragraph-s-reg italic text-greyscale-600">
        {{
          "help.howItWorks.sections.aiModule.results.recommendation" | transloco
        }}
      </p>
    </div>
  </app-section>

  <!-- Data Confidentiality -->
  <app-section
    [title]="'help.howItWorks.sections.confidentiality.title' | transloco"
    lucidIconRef="shield"
  >
    <div class="flex flex-col gap-4 pt-4">
      <p class="text-paragraph-reg text-greyscale-700">
        {{ "help.howItWorks.sections.confidentiality.intro" | transloco }}
      </p>

      <h3 class="mb-3 text-heading-4 font-semibold text-greyscale-900">
        {{ "help.howItWorks.sections.confidentiality.rules.title" | transloco }}
      </h3>
      <ul class="mb-4 space-y-2">
        <li
          class="flex items-start gap-2 text-paragraph-s-reg text-greyscale-700"
        >
          <lucide-icon
            name="check"
            class="mt-0.5 shrink-0 text-greyscale-500"
            [size]="16"
          />
          <span>{{
            "help.howItWorks.sections.confidentiality.rules.minimization"
              | transloco
          }}</span>
        </li>
        <li
          class="flex items-start gap-2 text-paragraph-s-reg text-greyscale-700"
        >
          <lucide-icon
            name="check"
            class="mt-0.5 shrink-0 text-greyscale-500"
            [size]="16"
          />
          <span>{{
            "help.howItWorks.sections.confidentiality.rules.access" | transloco
          }}</span>
        </li>
        <li
          class="flex items-start gap-2 text-paragraph-s-reg text-greyscale-700"
        >
          <lucide-icon
            name="check"
            class="mt-0.5 shrink-0 text-greyscale-500"
            [size]="16"
          />
          <span>
            {{
              "help.howItWorks.sections.confidentiality.rules.gdpr.before"
                | transloco
            }}
            <a
              href="#"
              (click)="openDocument('conditions', $event)"
              class="font-medium text-primary-600 underline hover:text-primary-800"
              >{{
                "help.howItWorks.sections.confidentiality.rules.gdpr.termsLink"
                  | transloco
              }}</a
            >
            {{
              "help.howItWorks.sections.confidentiality.rules.gdpr.middle"
                | transloco
            }}
            <a
              href="#"
              (click)="openDocument('privacy', $event)"
              class="font-medium text-primary-600 underline hover:text-primary-800"
              >{{
                "help.howItWorks.sections.confidentiality.rules.gdpr.privacyLink"
                  | transloco
              }}</a
            >
            {{
              "help.howItWorks.sections.confidentiality.rules.gdpr.after"
                | transloco
            }}
          </span>
        </li>
        <li
          class="flex items-start gap-2 text-paragraph-s-reg text-greyscale-700"
        >
          <lucide-icon
            name="check"
            class="mt-0.5 shrink-0 text-greyscale-500"
            [size]="16"
          />
          <span>{{
            "help.howItWorks.sections.confidentiality.rules.awareness"
              | transloco
          }}</span>
        </li>
      </ul>

      <div class="rounded-lg border border-warning-200 bg-warning-50 p-3">
        <div class="flex items-start gap-2">
          <lucide-icon
            name="triangle-alert"
            class="mt-0.5 shrink-0 text-warning-600"
            [size]="16"
          />
          <p class="text-paragraph-s-reg text-warning-800">
            {{
              "help.howItWorks.sections.confidentiality.recommendation"
                | transloco
            }}
          </p>
        </div>
      </div>
    </div>
  </app-section>

  <!-- Objectives -->
  <app-section
    [title]="'help.howItWorks.sections.objectives.title' | transloco"
    lucidIconRef="target"
  >
    <div class="flex flex-col gap-4 pt-4">
      <!-- Understanding -->
      <h3 class="mb-3 text-heading-4 font-semibold text-greyscale-900">
        {{
          "help.howItWorks.sections.objectives.understanding.title" | transloco
        }}
      </h3>
      <p class="mb-3 text-paragraph-reg text-greyscale-700">
        {{
          "help.howItWorks.sections.objectives.understanding.description"
            | transloco
        }}
      </p>
      <p class="mb-4 text-paragraph-reg text-greyscale-700">
        {{
          "help.howItWorks.sections.objectives.understanding.accountingIntro"
            | transloco
        }}
      </p>

      <!-- Carbon accounting cards -->
      <div class="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div class="rounded-lg border border-greyscale-200 p-4">
          <div class="mb-2 flex items-center gap-2">
            <lucide-icon
              name="bar-chart-2"
              class="text-primary-500"
              [size]="18"
            />
            <h4 class="text-heading-5 font-semibold text-greyscale-900">
              {{
                "help.howItWorks.sections.objectives.accounting.quantification.title"
                  | transloco
              }}
            </h4>
          </div>
          <p class="text-paragraph-s-reg text-greyscale-600">
            {{
              "help.howItWorks.sections.objectives.accounting.quantification.description"
                | transloco
            }}
          </p>
        </div>
        <div class="rounded-lg border border-greyscale-200 p-4">
          <div class="mb-2 flex items-center gap-2">
            <lucide-icon name="layers" class="text-primary-500" [size]="18" />
            <h4 class="text-heading-5 font-semibold text-greyscale-900">
              {{
                "help.howItWorks.sections.objectives.accounting.analysis.title"
                  | transloco
              }}
            </h4>
          </div>
          <p class="text-paragraph-s-reg text-greyscale-600">
            {{
              "help.howItWorks.sections.objectives.accounting.analysis.description"
                | transloco
            }}
          </p>
        </div>
        <div class="rounded-lg border border-greyscale-200 p-4">
          <div class="mb-2 flex items-center gap-2">
            <lucide-icon
              name="trending-up"
              class="text-primary-500"
              [size]="18"
            />
            <h4 class="text-heading-5 font-semibold text-greyscale-900">
              {{
                "help.howItWorks.sections.objectives.accounting.comparison.title"
                  | transloco
              }}
            </h4>
          </div>
          <p class="text-paragraph-s-reg text-greyscale-600">
            {{
              "help.howItWorks.sections.objectives.accounting.comparison.description"
                | transloco
            }}
          </p>
        </div>
        <div class="rounded-lg border border-greyscale-200 p-4">
          <div class="mb-2 flex items-center gap-2">
            <lucide-icon name="download" class="text-primary-500" [size]="18" />
            <h4 class="text-heading-5 font-semibold text-greyscale-900">
              {{
                "help.howItWorks.sections.objectives.accounting.reports.title"
                  | transloco
              }}
            </h4>
          </div>
          <p class="text-paragraph-s-reg text-greyscale-600">
            {{
              "help.howItWorks.sections.objectives.accounting.reports.description"
                | transloco
            }}
          </p>
        </div>
      </div>

      <!-- ADEME link -->
      <a
        href="https://www.youtube.com/watch?v=Ehml76xolZE"
        target="_blank"
        rel="noopener noreferrer"
        class="mb-6 flex items-center gap-2 text-paragraph-s-reg font-medium text-primary-600 hover:text-primary-800"
      >
        <lucide-icon name="external-link" [size]="16" />
        {{ "help.howItWorks.sections.objectives.ademeLink" | transloco }}
      </a>

      <!-- Goals list -->
      <p class="mb-3 text-paragraph-reg text-greyscale-700">
        {{ "help.howItWorks.sections.objectives.goals.intro" | transloco }}
      </p>
      <ul class="mb-6 list-none space-y-2">
        <li class="flex items-start gap-2">
          <lucide-icon
            name="check-circle"
            class="mt-0.5 shrink-0 text-validation-600"
            [size]="18"
          />
          <span class="text-greyscale-700">{{
            "help.howItWorks.sections.objectives.goals.communicate" | transloco
          }}</span>
        </li>
        <li class="flex items-start gap-2">
          <lucide-icon
            name="check-circle"
            class="mt-0.5 shrink-0 text-validation-600"
            [size]="18"
          />
          <span class="text-greyscale-700">{{
            "help.howItWorks.sections.objectives.goals.reporting" | transloco
          }}</span>
        </li>
        <li class="flex items-start gap-2">
          <lucide-icon
            name="check-circle"
            class="mt-0.5 shrink-0 text-validation-600"
            [size]="18"
          />
          <span class="text-greyscale-700">{{
            "help.howItWorks.sections.objectives.goals.reduction" | transloco
          }}</span>
        </li>
        <li class="flex items-start gap-2">
          <lucide-icon
            name="check-circle"
            class="mt-0.5 shrink-0 text-validation-600"
            [size]="18"
          />
          <span class="text-greyscale-700">{{
            "help.howItWorks.sections.objectives.goals.keys" | transloco
          }}</span>
        </li>
      </ul>

      <!-- Eco-score -->
      <div class="rounded-lg border border-primary-200 bg-primary-50 p-4">
        <div class="flex items-start gap-3">
          <lucide-icon
            name="award"
            class="mt-0.5 shrink-0 text-primary-500"
            [size]="22"
          />
          <div>
            <h4 class="text-heading-5 mb-2 font-semibold text-primary-900">
              {{
                "help.howItWorks.sections.objectives.ecoscore.title" | transloco
              }}
            </h4>
            <p class="mb-3 text-paragraph-s-reg text-primary-800">
              {{
                "help.howItWorks.sections.objectives.ecoscore.description"
                  | transloco
              }}
            </p>
            <p class="mb-3 text-paragraph-s-reg text-primary-800">
              {{
                "help.howItWorks.sections.objectives.ecoscore.details"
                  | transloco
              }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </app-section>

  <!-- Tool Operation -->
  <app-section
    [title]="'help.howItWorks.sections.toolOperation.title' | transloco"
    lucidIconRef="calculator"
  >
    <div class="flex flex-col gap-4 pt-4">
      <h3 class="mb-3 text-heading-4 font-semibold text-greyscale-900">
        {{
          "help.howItWorks.sections.toolOperation.whatWeCount.title" | transloco
        }}
      </h3>
      <p class="mb-6 text-paragraph-reg text-greyscale-700">
        {{
          "help.howItWorks.sections.toolOperation.whatWeCount.description"
            | transloco
        }}
      </p>

      <h3 class="mb-4 text-heading-4 font-semibold text-greyscale-900">
        {{
          "help.howItWorks.sections.toolOperation.formulas.title" | transloco
        }}
      </h3>

      <div class="space-y-4">
        <!-- Transport -->
        <div class="rounded-lg border border-greyscale-200 p-4">
          <div class="mb-3 flex items-center gap-2">
            <lucide-icon name="plane" class="text-primary-500" [size]="20" />
            <h4 class="text-heading-5 font-semibold text-greyscale-900">
              {{
                "help.howItWorks.sections.toolOperation.categories.transport.title"
                  | transloco
              }}
            </h4>
          </div>
          <p class="mb-3 text-paragraph-s-reg text-greyscale-700">
            {{
              "help.howItWorks.sections.toolOperation.categories.transport.description"
                | transloco
            }}
          </p>
          <div
            class="rounded bg-greyscale-100 p-3 font-mono text-paragraph-s-reg"
          >
            <p class="mb-1">
              <strong>{{
                "help.howItWorks.sections.toolOperation.categories.transport.formula1Label"
                  | transloco
              }}</strong>
            </p>
            <p class="mb-2 text-primary-700">
              {{
                "help.howItWorks.sections.toolOperation.categories.transport.formula1Value"
                  | transloco
              }}
            </p>
            <p class="mb-1">
              <strong>{{
                "help.howItWorks.sections.toolOperation.categories.transport.formula2Label"
                  | transloco
              }}</strong>
            </p>
            <p class="text-primary-700">
              {{
                "help.howItWorks.sections.toolOperation.categories.transport.formula2Value"
                  | transloco
              }}
            </p>
          </div>
        </div>

        <!-- Accommodation -->
        <div class="rounded-lg border border-greyscale-200 p-4">
          <div class="mb-3 flex items-center gap-2">
            <lucide-icon name="home" class="text-primary-500" [size]="20" />
            <h4 class="text-heading-5 font-semibold text-greyscale-900">
              {{
                "help.howItWorks.sections.toolOperation.categories.accommodation.title"
                  | transloco
              }}
            </h4>
          </div>
          <p class="text-paragraph-s-reg text-greyscale-700">
            {{
              "help.howItWorks.sections.toolOperation.categories.accommodation.description"
                | transloco
            }}
          </p>
        </div>

        <!-- Activities -->
        <div class="rounded-lg border border-greyscale-200 p-4">
          <div class="mb-3 flex items-center gap-2">
            <lucide-icon name="compass" class="text-primary-500" [size]="20" />
            <h4 class="text-heading-5 font-semibold text-greyscale-900">
              {{
                "help.howItWorks.sections.toolOperation.categories.activities.title"
                  | transloco
              }}
            </h4>
          </div>
          <p class="text-paragraph-s-reg text-greyscale-700">
            {{
              "help.howItWorks.sections.toolOperation.categories.activities.description"
                | transloco
            }}
          </p>
        </div>

        <!-- Food -->
        <div class="rounded-lg border border-greyscale-200 p-4">
          <div class="mb-3 flex items-center gap-2">
            <lucide-icon name="utensils" class="text-primary-500" [size]="20" />
            <h4 class="text-heading-5 font-semibold text-greyscale-900">
              {{
                "help.howItWorks.sections.toolOperation.categories.food.title"
                  | transloco
              }}
            </h4>
          </div>
          <p class="text-paragraph-s-reg text-greyscale-700">
            {{
              "help.howItWorks.sections.toolOperation.categories.food.description"
                | transloco
            }}
          </p>
        </div>
      </div>
      @if (!packLimitationService.isDiscovery()) {
        <div
          class="mt-4 flex items-center justify-between rounded-lg border border-greyscale-200 bg-primary-50 px-4 py-3"
        >
          <div>
            <p class="text-paragraph-s-med font-semibold text-primary-900">
              {{
                "help.howItWorks.sections.toolOperation.methodology.title"
                  | transloco
              }}
            </p>
            <p class="text-paragraph-s-reg text-primary-700">
              {{
                "help.howItWorks.sections.toolOperation.methodology.description"
                  | transloco
              }}
            </p>
          </div>
          <button
            type="button"
            (click)="methodologyService.download()"
            [disabled]="methodologyService.loading()"
            class="p-button p-button-outlined justify-center"
          >
            <lucide-icon name="download" [size]="14" />
            {{
              "help.howItWorks.sections.toolOperation.methodology.link"
                | transloco
            }}
          </button>
        </div>
      }
    </div>
  </app-section>

  <!-- GHG Quantification -->
  <app-section
    [title]="'help.howItWorks.sections.quantification.title' | transloco"
    lucidIconRef="flask-conical"
  >
    <div class="flex flex-col gap-4 pt-4">
      <h3 class="mb-3 text-heading-4 font-semibold text-greyscale-900">
        {{
          "help.howItWorks.sections.quantification.ghgTypes.title" | transloco
        }}
      </h3>
      <p class="mb-4 text-paragraph-reg text-greyscale-700">
        {{
          "help.howItWorks.sections.quantification.ghgTypes.kyoto" | transloco
        }}
      </p>
      <p class="mb-4 text-paragraph-reg text-greyscale-700">
        {{
          "help.howItWorks.sections.quantification.ghgTypes.act4travel"
            | transloco
        }}
      </p>

      <!-- CO2e & GWP -->
      <p class="mb-4 text-paragraph-reg text-greyscale-700">
        {{
          "help.howItWorks.sections.quantification.co2e.description" | transloco
        }}
      </p>
      <ul class="mb-4 space-y-1.5 pl-1">
        <li
          class="flex items-start gap-2 text-paragraph-s-reg text-greyscale-700"
        >
          <span
            class="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"
          ></span>
          {{
            "help.howItWorks.sections.quantification.co2e.co2Prg" | transloco
          }}
        </li>
        <li
          class="flex items-start gap-2 text-paragraph-s-reg text-greyscale-700"
        >
          <span
            class="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"
          ></span>
          {{
            "help.howItWorks.sections.quantification.co2e.ch4Prg" | transloco
          }}
        </li>
        <li
          class="flex items-start gap-2 text-paragraph-s-reg text-greyscale-700"
        >
          <span
            class="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"
          ></span>
          {{
            "help.howItWorks.sections.quantification.co2e.n2oPrg" | transloco
          }}
        </li>
      </ul>
      <p class="mb-6 text-paragraph-s-reg italic text-greyscale-600">
        {{ "help.howItWorks.sections.quantification.co2e.n2oNote" | transloco }}
      </p>

      <!-- Radiative Forcing -->
      <div class="rounded-lg border border-warning-200 bg-warning-50 p-4">
        <div class="flex items-start gap-3">
          <lucide-icon
            name="plane"
            class="mt-0.5 shrink-0 text-warning-600"
            [size]="20"
          />
          <div>
            <h4 class="text-heading-5 mb-2 font-semibold text-warning-900">
              {{
                "help.howItWorks.sections.quantification.radiativeForcing.title"
                  | transloco
              }}
            </h4>
            <p class="text-paragraph-s-reg text-warning-800">
              {{
                "help.howItWorks.sections.quantification.radiativeForcing.description"
                  | transloco
              }}
              {{
                "help.howItWorks.sections.quantification.radiativeForcing.sourceLabel"
                  | transloco
              }}
              <a
                [href]="ademeAirUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="underline hover:opacity-80"
                >{{
                  "help.howItWorks.sections.quantification.radiativeForcing.sourceLink"
                    | transloco
                }}</a
              >.
            </p>
          </div>
        </div>
      </div>
    </div>
  </app-section>

  <!-- Tool Principles -->
  <app-section
    [title]="'help.howItWorks.sections.principles.title' | transloco"
    lucidIconRef="settings"
  >
    <div class="flex flex-col gap-4 pt-4">
      <!-- How we calculate -->
      <h3 class="mb-3 text-heading-4 font-semibold text-greyscale-900">
        {{
          "help.howItWorks.sections.principles.howWeCalculate.title" | transloco
        }}
      </h3>
      <p class="mb-6 text-paragraph-reg text-greyscale-700">
        {{
          "help.howItWorks.sections.principles.howWeCalculate.description"
            | transloco
        }}
      </p>

      <!-- Emission factors box -->
      <div class="mb-6 rounded-lg bg-greyscale-100 p-4">
        <p class="mb-2 font-semibold text-greyscale-900">
          {{
            "help.howItWorks.sections.principles.emissionFactor.title"
              | transloco
          }}
        </p>
        <p class="mb-2 text-paragraph-s-reg text-greyscale-700">
          {{
            "help.howItWorks.sections.principles.emissionFactor.description"
              | transloco
          }}
        </p>
        <p class="text-paragraph-s-reg font-medium text-primary-600">
          {{
            "help.howItWorks.sections.principles.emissionFactor.update"
              | transloco
          }}
        </p>
      </div>

      <!-- Deviations from Bilan Carbone -->
      <h3 class="mb-2 text-heading-3 font-semibold text-greyscale-900">
        {{ "help.howItWorks.sections.principles.deviations.title" | transloco }}
      </h3>
      <p class="mb-6 text-paragraph-reg text-greyscale-700">
        {{
          "help.howItWorks.sections.principles.deviations.description"
            | transloco
        }}
      </p>

      <!-- Point 1: Scope -->
      <h4 class="mb-3 text-heading-4 font-semibold text-greyscale-800">
        {{
          "help.howItWorks.sections.principles.deviations.point1Title"
            | transloco
        }}
      </h4>
      <div class="mb-6 space-y-3">
        <p class="text-paragraph-reg text-greyscale-700">
          {{
            "help.howItWorks.sections.principles.unofficialSources.scope3"
              | transloco
          }}
        </p>
        <p class="text-paragraph-reg text-greyscale-700">
          {{
            "help.howItWorks.sections.principles.unofficialSources.exclusions"
              | transloco
          }}
        </p>
        <p class="text-paragraph-reg text-greyscale-700">
          {{ "help.howItWorks.sections.principles.currentScope" | transloco }}
        </p>
      </div>

      <!-- Point 2: Other methodologies -->
      <h4 class="mb-2 text-heading-4 font-semibold text-greyscale-800">
        {{
          "help.howItWorks.sections.principles.otherSources.title" | transloco
        }}
      </h4>
      <p class="mb-6 text-paragraph-reg text-greyscale-700">
        {{
          "help.howItWorks.sections.principles.otherSources.description"
            | transloco
        }}
      </p>

      <!-- Point 3: Unofficial sources -->
      <h4 class="mb-3 text-heading-4 font-semibold text-greyscale-800">
        {{
          "help.howItWorks.sections.principles.unofficialSources.title"
            | transloco
        }}
      </h4>
      <div class="mb-6 space-y-3">
        <p class="text-paragraph-reg text-greyscale-700">
          {{
            "help.howItWorks.sections.principles.unofficialSources.cruise"
              | transloco
          }}
        </p>
        <p class="text-paragraph-reg text-greyscale-700">
          {{
            "help.howItWorks.sections.principles.unofficialSources.ferry"
              | transloco
          }}
        </p>
        <p class="text-paragraph-reg text-greyscale-700">
          {{
            "help.howItWorks.sections.principles.unofficialSources.lcaVsBilanCarbone"
              | transloco
          }}
        </p>
      </div>

      <!-- Point 4: Ratios -->
      <h4 class="mb-2 text-heading-4 font-semibold text-greyscale-800">
        {{ "help.howItWorks.sections.principles.ratios.title" | transloco }}
      </h4>
      <p class="mb-2 text-paragraph-reg text-greyscale-700">
        {{
          "help.howItWorks.sections.principles.ratios.description" | transloco
        }}
      </p>
      <p class="mb-2 text-paragraph-reg text-greyscale-700">
        {{
          "help.howItWorks.sections.principles.ratios.physicalRatio" | transloco
        }}
      </p>
      <p class="mb-2 text-paragraph-reg text-greyscale-700">
        {{
          "help.howItWorks.sections.principles.ratios.noLocalPurchases"
            | transloco
        }}
      </p>
      <p class="mb-6 text-paragraph-reg text-greyscale-700">
        {{
          "help.howItWorks.sections.principles.ratios.monetaryCase" | transloco
        }}
      </p>

      <!-- Point 5: Uncertainty -->
      <h4 class="mb-2 text-heading-4 font-semibold text-greyscale-800">
        {{
          "help.howItWorks.sections.principles.uncertainty.title" | transloco
        }}
      </h4>
      <p class="mb-3 text-paragraph-reg text-greyscale-700">
        {{
          "help.howItWorks.sections.principles.uncertainty.description"
            | transloco
        }}
      </p>
      <p class="mb-2 text-paragraph-reg font-semibold text-greyscale-800">
        {{
          "help.howItWorks.sections.principles.uncertainty.causesTitle"
            | transloco
        }}
      </p>
      <ul class="mb-4 space-y-2">
        <li
          class="flex items-start gap-2 text-paragraph-reg text-greyscale-700"
        >
          <span
            class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"
          ></span>
          {{
            "help.howItWorks.sections.principles.uncertainty.technical"
              | transloco
          }}
        </li>
        <li
          class="flex items-start gap-2 text-paragraph-reg text-greyscale-700"
        >
          <span
            class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"
          ></span>
          {{
            "help.howItWorks.sections.principles.uncertainty.geographic"
              | transloco
          }}
        </li>
        <li
          class="flex items-start gap-2 text-paragraph-reg text-greyscale-700"
        >
          <span
            class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"
          ></span>
          {{
            "help.howItWorks.sections.principles.uncertainty.temporal"
              | transloco
          }}
        </li>
        <li
          class="flex items-start gap-2 text-paragraph-reg text-greyscale-700"
        >
          <span
            class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"
          ></span>
          {{
            "help.howItWorks.sections.principles.uncertainty.completeness"
              | transloco
          }}
        </li>
        <li
          class="flex items-start gap-2 text-paragraph-reg text-greyscale-700"
        >
          <span
            class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"
          ></span>
          {{
            "help.howItWorks.sections.principles.uncertainty.reliability"
              | transloco
          }}
        </li>
      </ul>
      <p class="text-paragraph-reg italic text-greyscale-600">
        {{
          "help.howItWorks.sections.principles.uncertainty.methodologyDoc"
            | transloco
        }}
      </p>
    </div>
  </app-section>

  <!-- Net Zero Initiative -->
  <app-section
    [title]="'help.howItWorks.sections.netZero.title' | transloco"
    lucidIconRef="globe"
  >
    <div class="flex flex-col gap-4 pt-4">
      <p class="mb-4 text-paragraph-reg text-greyscale-700">
        {{ "help.howItWorks.sections.netZero.intro" | transloco }}
      </p>

      <p class="mb-6 text-paragraph-reg text-greyscale-700">
        {{ "help.howItWorks.sections.netZero.principles" | transloco }}
        {{ "help.howItWorks.sections.netZero.sourceLabel" | transloco }}
        <a
          [href]="nziUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-primary-600 underline hover:text-primary-800"
          >{{ "help.howItWorks.sections.netZero.sourceLink" | transloco }}</a
        >.
      </p>

      <!-- Three Pillars -->
      <h3 class="mb-4 text-heading-4 font-semibold text-greyscale-900">
        {{ "help.howItWorks.sections.netZero.threePillars.title" | transloco }}
      </h3>

      <div class="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        @for (pillar of netZeroPillars; track pillar.letterKey) {
          <div class="rounded-lg border border-greyscale-200 p-4">
            <div class="mb-3 flex items-center gap-3">
              <div
                [class]="
                  'flex h-8 w-8 items-center justify-center rounded-full text-paragraph-reg font-bold text-white ' +
                  pillar.color
                "
              >
                {{ pillar.letterKey }}
              </div>
              <h4 class="text-heading-5 font-semibold text-greyscale-900">
                {{ pillar.titleKey | transloco }}
              </h4>
            </div>
            <p class="text-paragraph-reg text-greyscale-600">
              {{ pillar.descriptionKey | transloco }}
            </p>
          </div>
        }
      </div>

      <!-- Key Principles -->
      <div class="rounded-lg border border-validation-200 bg-validation-50 p-4">
        <h4 class="text-heading-5 mb-3 font-semibold text-validation-900">
          {{
            "help.howItWorks.sections.netZero.keyPrinciples.title" | transloco
          }}
        </h4>
        <ul class="space-y-2">
          <li
            class="flex items-start gap-2 text-paragraph-reg text-validation-800"
          >
            <lucide-icon
              name="check"
              class="mt-0.5 shrink-0 text-validation-600"
              [size]="16"
            />
            <span>{{
              "help.howItWorks.sections.netZero.keyPrinciples.definition"
                | transloco
            }}</span>
          </li>
          <li
            class="flex items-start gap-2 text-paragraph-reg text-validation-800"
          >
            <lucide-icon
              name="check"
              class="mt-0.5 shrink-0 text-validation-600"
              [size]="16"
            />
            <span>{{
              "help.howItWorks.sections.netZero.keyPrinciples.strategy"
                | transloco
            }}</span>
          </li>
          <li
            class="flex items-start gap-2 text-paragraph-reg text-validation-800"
          >
            <lucide-icon
              name="check"
              class="mt-0.5 shrink-0 text-validation-600"
              [size]="16"
            />
            <span>{{
              "help.howItWorks.sections.netZero.keyPrinciples.actionTypes"
                | transloco
            }}</span>
          </li>
          <li
            class="flex items-start gap-2 text-paragraph-reg text-validation-800"
          >
            <lucide-icon
              name="check"
              class="mt-0.5 shrink-0 text-validation-600"
              [size]="16"
            />
            <span>{{
              "help.howItWorks.sections.netZero.keyPrinciples.priority"
                | transloco
            }}</span>
          </li>
          <li
            class="flex items-start gap-2 text-paragraph-reg text-validation-800"
          >
            <lucide-icon
              name="check"
              class="mt-0.5 shrink-0 text-validation-600"
              [size]="16"
            />
            <span>{{
              "help.howItWorks.sections.netZero.keyPrinciples.quantify"
                | transloco
            }}</span>
          </li>
          <li
            class="flex items-start gap-2 text-paragraph-reg text-validation-800"
          >
            <lucide-icon
              name="check"
              class="mt-0.5 shrink-0 text-validation-600"
              [size]="16"
            />
            <span>{{
              "help.howItWorks.sections.netZero.keyPrinciples.science"
                | transloco
            }}</span>
          </li>
          <li
            class="flex items-start gap-2 text-paragraph-reg text-validation-800"
          >
            <lucide-icon
              name="check"
              class="mt-0.5 shrink-0 text-validation-600"
              [size]="16"
            />
            <span>{{
              "help.howItWorks.sections.netZero.keyPrinciples.results"
                | transloco
            }}</span>
          </li>
          <li
            class="flex items-start gap-2 text-paragraph-reg text-validation-800"
          >
            <lucide-icon
              name="check"
              class="mt-0.5 shrink-0 text-validation-600"
              [size]="16"
            />
            <span>{{
              "help.howItWorks.sections.netZero.keyPrinciples.avoidedEmissions"
                | transloco
            }}</span>
          </li>
          <li
            class="flex items-start gap-2 text-paragraph-reg text-validation-800"
          >
            <lucide-icon
              name="check"
              class="mt-0.5 shrink-0 text-validation-600"
              [size]="16"
            />
            <span>{{
              "help.howItWorks.sections.netZero.keyPrinciples.sinks" | transloco
            }}</span>
          </li>
          <li
            class="flex items-start gap-2 text-paragraph-reg text-validation-800"
          >
            <lucide-icon
              name="check"
              class="mt-0.5 shrink-0 text-validation-600"
              [size]="16"
            />
            <span>{{
              "help.howItWorks.sections.netZero.keyPrinciples.communicate"
                | transloco
            }}</span>
          </li>
        </ul>
      </div>
    </div>
  </app-section>

  <!-- Carbon Contribution -->
  <app-section
    [title]="'help.howItWorks.sections.carbonContribution.title' | transloco"
    lucidIconRef="hand-coins"
  >
    <div class="flex flex-col gap-6 pt-4">
      <!-- Reduction first, then contribution -->
      <div class="flex flex-col gap-3">
        <p class="text-paragraph-reg text-greyscale-700">
          {{
            "help.howItWorks.sections.carbonContribution.intro.priority"
              | transloco
          }}
        </p>
        <p class="text-paragraph-reg text-greyscale-700">
          {{
            "help.howItWorks.sections.carbonContribution.intro.influence"
              | transloco
          }}
        </p>
        <p class="text-paragraph-reg text-greyscale-700">
          {{
            "help.howItWorks.sections.carbonContribution.intro.complement"
              | transloco
          }}
        </p>
        <p class="text-paragraph-reg text-greyscale-700">
          {{
            "help.howItWorks.sections.carbonContribution.intro.sdg" | transloco
          }}
        </p>
      </div>

      <!-- Climate project types -->
      <div>
        <p
          class="mb-4 rounded-lg border border-primary-200 bg-primary-50 p-4 text-paragraph-reg text-greyscale-800"
        >
          {{
            "help.howItWorks.sections.carbonContribution.intro.everyTrip"
              | transloco
          }}
        </p>

        <h3 class="mb-4 text-heading-4 font-semibold text-greyscale-900">
          {{
            "help.howItWorks.sections.carbonContribution.projectTypes.title"
              | transloco
          }}
        </h3>

        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          @for (projectType of climateProjectTypes; track projectType.nameKey) {
            <div class="rounded-lg border border-greyscale-200 bg-white p-5">
              <div class="mb-3 flex items-center gap-3">
                <div
                  class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-200"
                >
                  <lucide-icon
                    [name]="projectType.icon"
                    class="text-primary-500"
                    [size]="20"
                  />
                </div>
                <h4 class="text-heading-5 font-semibold text-greyscale-900">
                  {{ projectType.nameKey | transloco }}
                </h4>
              </div>
              <p class="text-paragraph-s-reg text-greyscale-600">
                {{ projectType.descriptionKey | transloco }}
              </p>
            </div>
          }
        </div>
      </div>

      <!-- Guarantee -->
      <div class="rounded-lg border border-validation-200 bg-validation-50 p-4">
        <h4 class="text-heading-5 mb-3 font-semibold text-validation-900">
          {{
            "help.howItWorks.sections.carbonContribution.guarantee.title"
              | transloco
          }}
        </h4>
        <p class="mb-3 text-paragraph-reg text-validation-800">
          {{
            "help.howItWorks.sections.carbonContribution.guarantee.hundredPercent"
              | transloco
          }}
        </p>
        <p class="mb-3 text-paragraph-reg text-validation-800">
          {{
            "help.howItWorks.sections.carbonContribution.guarantee.intro"
              | transloco
          }}
        </p>
        <ul class="space-y-2">
          <li
            class="flex items-start gap-2 text-paragraph-reg text-validation-800"
          >
            <lucide-icon
              name="check"
              class="mt-0.5 shrink-0 text-validation-600"
              [size]="16"
            />
            <span>{{
              "help.howItWorks.sections.carbonContribution.guarantee.developingCountries"
                | transloco
            }}</span>
          </li>
          <li
            class="flex items-start gap-2 text-paragraph-reg text-validation-800"
          >
            <lucide-icon
              name="check"
              class="mt-0.5 shrink-0 text-validation-600"
              [size]="16"
            />
            <span>{{
              "help.howItWorks.sections.carbonContribution.guarantee.europe"
                | transloco
            }}</span>
          </li>
        </ul>
      </div>

      <!-- Project selection -->
      <div>
        <h3 class="mb-3 text-heading-4 font-semibold text-greyscale-900">
          {{
            "help.howItWorks.sections.carbonContribution.selection.title"
              | transloco
          }}
        </h3>
        <div class="flex flex-col gap-3">
          <p class="text-paragraph-reg text-greyscale-700">
            {{
              "help.howItWorks.sections.carbonContribution.selection.criteria"
                | transloco
            }}
          </p>
          <p class="text-paragraph-reg text-greyscale-700">
            {{
              "help.howItWorks.sections.carbonContribution.selection.partners"
                | transloco
            }}
          </p>
        </div>
      </div>

      <!-- Certificate and audit -->
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div class="rounded-lg border border-greyscale-200 p-4">
          <div class="mb-3 flex items-center gap-2">
            <lucide-icon name="award" class="text-primary-500" [size]="20" />
            <h3 class="text-heading-5 font-semibold text-greyscale-900">
              {{
                "help.howItWorks.sections.carbonContribution.transparency.certificate.title"
                  | transloco
              }}
            </h3>
          </div>
          <p class="text-paragraph-s-reg text-greyscale-700">
            {{
              "help.howItWorks.sections.carbonContribution.transparency.certificate.description"
                | transloco
            }}
          </p>
        </div>

        <div class="rounded-lg border border-greyscale-200 p-4">
          <div class="mb-3 flex items-center gap-2">
            <lucide-icon
              name="shield-check"
              class="text-primary-500"
              [size]="20"
            />
            <h3 class="text-heading-5 font-semibold text-greyscale-900">
              {{
                "help.howItWorks.sections.carbonContribution.transparency.audit.title"
                  | transloco
              }}
            </h3>
          </div>
          <p class="text-paragraph-s-reg text-greyscale-700">
            {{
              "help.howItWorks.sections.carbonContribution.transparency.audit.description"
                | transloco
            }}
          </p>
        </div>
      </div>
    </div>
  </app-section>

  <!-- Data Sources -->
  <!-- <section class="rounded-lg border border-greyscale-200 bg-white p-4">
    <div class="mb-4 flex items-center gap-3">
      <div
        class="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50"
      >
        <lucide-icon name="database" class="text-primary-500" [size]="22" />
      </div>
      <h2 class="text-heading-3 font-semibold text-greyscale-950">
        {{ "help.howItWorks.dataSources.title" | transloco }}
      </h2>
    </div>

    <p class="mb-4 text-paragraph-reg text-greyscale-600">
      {{ "help.howItWorks.dataSources.description" | transloco }}
    </p>

    <div class="flex flex-wrap gap-3">
      <span
        class="rounded-lg bg-greyscale-100 px-4 py-2 text-paragraph-reg font-medium text-greyscale-700"
        >ADEME</span
      >
      <span
        class="rounded-lg bg-greyscale-100 px-4 py-2 text-paragraph-reg font-medium text-greyscale-700"
        >DEFRA</span
      >
      <span
        class="rounded-lg bg-greyscale-100 px-4 py-2 text-paragraph-reg font-medium text-greyscale-700"
        >ICAO</span
      >
      <span
        class="rounded-lg bg-greyscale-100 px-4 py-2 text-paragraph-reg font-medium text-greyscale-700"
        >GHG Protocol</span
      >
      <span
        class="rounded-lg bg-greyscale-100 px-4 py-2 text-paragraph-reg font-medium text-greyscale-700"
        >Base Carbone</span
      >
      <span
        class="rounded-lg bg-greyscale-100 px-4 py-2 text-paragraph-reg font-medium text-greyscale-700"
        >SBTi</span
      >
    </div>
  </section> -->
</div>
